import React, { useState, useRef } from 'react';
import { IoIosSearch } from 'react-icons/io';
import { Input } from '@/components/ui/input';
import { useDebounce } from '@/hooks/debounce.hook';
import { cn } from '@/utils/classnames';

export interface SearchProps<T> {
  data?: T[];
  onSearchChange?: (query: string) => void;
  placeholder?: string;
  className?: string;
  debounceDelay?: number;
  showIcon?: boolean;
  variant?: 'header' | 'global';
  autoFocus?: boolean;
}

export const Search = <T extends Record<string, any>>({
  onSearchChange,
  placeholder = 'Tìm kiếm...',
  className = '',
  debounceDelay = 100,
  showIcon = true,
  variant = 'global',
  autoFocus = false,
}: SearchProps<T>) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const debouncedSearchTerm = useDebounce(searchTerm, debounceDelay);
  const isMountedRef = useRef(false);

  // const filteredData = useMemo(() => {
  //   // If there is no search term, return all data
  //   if (!debouncedSearchTerm || debouncedSearchTerm.trim() === '') {
  //     return data;
  //   }

  //   const searchTermLower = debouncedSearchTerm.trim().toLowerCase();

  //   // Filter data based on the search term
  //   const result = data.filter((item) => {
  //     // Iterate through every object key
  //     const found = Object.keys(item).some((key) => {
  //       const value = item[key];

  //       // Search only when value is not null/undefined
  //       if (value != null) {
  //         const stringValue = String(value).toLowerCase();

  //         // Use word boundaries to search exact keywords
  //         // Avoid cases where "active" matches "inactive"
  //         const regex = new RegExp(
  //           `\\b${searchTermLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
  //           'i',
  //         );
  //         const matches = regex.test(stringValue);

  //         if (matches) return matches;
  //       }

  //       return false;
  //     });

  //     if (found) return found;
  //   });

  //   return result;
  // }, [data, debouncedSearchTerm]);

  // Call onSearchResults only when search term changes
  // Only when search term changes, not when filteredData changes

  // Call onSearchChange when search term changes
  const onSearchChangeRef = React.useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  React.useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    if (onSearchChange) {
      onSearchChange(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const variantClassName =
    variant === 'header'
      ? 'h-[4rem] max-w-[30rem] rounded-[5rem] bg-[#F4F7FE] px-[1.2rem] py-[1rem] shadow-none dark:bg-[#17233A]'
      : 'max-w-[42.6rem] rounded-[1rem] border border-search bg-box px-[1.2rem] py-[1rem]';

  const inputVariantClassName =
    variant === 'header'
      ? 'h-[2.4rem] text-[1.4rem] font-normal leading-[1.9rem] tracking-[0.007rem]'
      : 'h-[2.5rem]';

  const iconColorClassName =
    variant === 'header'
      ? isFocused
        ? 'text-[#3463FD]'
        : 'text-[#7988A4]'
      : isFocused
        ? 'text-[#3463FD]'
        : 'text-[#333945] dark:text-[#A1ACC0]';

  return (
    <div
      className={cn(
        'group relative flex w-full items-center overflow-hidden transition-colors duration-200',
        variantClassName,
        'focus-within:border-[#3463FD] focus-within:shadow-[0px_0px_6px_0px_rgba(0,0,0,0.06)]',
        variant === 'header' && 'h-[4rem] rounded-full bg-[#F4F7FE] border-transparent dark:bg-[#17233A]',
        className,
      )}
    >
      {showIcon && (
        <div className="transition-colors duration-200">
          <IoIosSearch size={20} className={iconColorClassName} />
        </div>
      )}

      <Input
        type="text"
        placeholder={placeholder}
        autoFocus={autoFocus}
        value={searchTerm}
        onChange={handleSearchChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={cn(
          'border-transparent bg-transparent text-[#111827] transition-colors duration-200 placeholder:text-[#111827]/60 dark:text-[#E9EFFD] dark:placeholder:text-[#9FB0D0]',
          inputVariantClassName,
          isFocused ? 'placeholder:text-[#111827]/60' : 'placeholder:text-[#111827]/60',
        )}
      />

      {searchTerm && (
        <button
          onClick={handleClearSearch}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          type="button"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={5}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
};
